// Configurazione URL delle API
const API_CONFIG = {
  localBackend: import.meta.env.VITE_API_LOCAL_BACKEND || 'http://localhost:5034',
  backend: import.meta.env.VITE_API_BACKEND || '' // Empty string usa path relativo (stesso dominio)
}

// Funzione principale per le chiamate API
export async function apiCall(endpoint, options = {}) {
  const baseUrl = options.backend === 'local' ? API_CONFIG.localBackend : API_CONFIG.backend
  const url = `${baseUrl}${endpoint}`
  
  // Headers di default
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...options.headers
  }
  
  // Aggiungi automaticamente il token JWT se presente e non disabilitato
  if (options.includeAuth !== false) {
    const token = localStorage.getItem('auth_token')
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`
    }
  }
  
  const defaultOptions = {
    headers: defaultHeaders,
    ...options
  }
  
  try {
    const response = await fetch(url, defaultOptions)
    
    // Se la richiesta fallisce con 401 (token scaduto) e non è già una richiesta di login/refresh
    if (response.status === 401 && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/refresh')) {
      // Prova a rinnovare il token
      const refreshResult = await refreshToken()
      if (refreshResult.success) {
        // Riprova la richiesta originale con il nuovo token
        defaultHeaders['Authorization'] = `Bearer ${refreshResult.token}`
        const retryResponse = await fetch(url, { ...defaultOptions, headers: defaultHeaders })
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({}))
          throw new Error(`HTTP error! status: ${retryResponse.status}, message: ${errorData.message || JSON.stringify(errorData)}`)
        }
        return await retryResponse.json()
      } else {
        // Se il refresh fallisce, redirect al login
        window.location.href = '/login'
        throw new Error('Session expired, please login again')
      }
    }
    
    if (!response.ok) {
      // Prova a leggere il corpo della risposta per ottenere dettagli dell'errore
      const errorData = await response.json().catch(() => ({}))
      
      // Create error object with additional information
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`)
      error.response = {
        status: response.status,
        data: errorData
      }
      error.error = errorData.error
      
      throw error
    }
    return await response.json()
  } catch (error) {
    console.error('API call failed:', error)
    throw error
  }
}

// Funzione per rinnovare il token
async function refreshToken() {
  try {
    const currentToken = localStorage.getItem('auth_token')
    if (!currentToken) {
      return { success: false }
    }
    
    const response = await fetch(`${API_CONFIG.backend}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      localStorage.setItem('auth_token', data.token)
      return { success: true, token: data.token }
    } else {
      localStorage.removeItem('auth_token')
      return { success: false }
    }
  } catch (error) {
    console.error('Token refresh failed:', error)
    localStorage.removeItem('auth_token')
    return { success: false }
  }
}

// API specifiche per autenticazione
export const authAPI = {
  login: (credentials) => apiCall('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
    includeAuth: false
  }),
  
  verify: () => apiCall('/api/auth/verify', {
    method: 'POST'
  }),
  
  refresh: () => apiCall('/api/auth/refresh', {
    method: 'POST'
  })
}

// API specifiche per i percorsi (LocalBackend)
export const pathsAPI = {
  getConfiguredPaths: () => apiCall('/api/paths', {
    backend: 'local'
  }),
  
  addPath: (path) => apiCall('/api/paths', {
    method: 'POST',
    body: JSON.stringify(path),
    backend: 'local'
  }),
  
  removePath: (index) => apiCall(`/api/paths/${index}`, {
    method: 'DELETE',
    backend: 'local'
  }),
  
  getFolders: (index) => apiCall(`/api/paths/${index}/folders`, {
    backend: 'local'
  })
}

// Esporta anche la configurazione per facilità di accesso
export { API_CONFIG }